/**
 * « Copier » dans la fiche d'une entree : le bouton ne porte qu'une REFERENCE au champ (`data-field`), jamais sa
 * valeur. La valeur est lue dans les secrets dechiffres de l'entree au moment du clic.
 *
 * Pourquoi : un secret masque a l'ecran (`••••`) mais pose dans un attribut `data-value` reste lisible par tout ce
 * qui lit la page -- extension navigateur, lecteur d'ecran qui annonce les attributs, inspecteur, capture du DOM
 * d'un outil de support. Masque doit vouloir dire ABSENT de la page, comme la cle privee SSH et la phrase de
 * recuperation d'un portefeuille (Bitwarden et 1Password lisent aussi la valeur dans le modele au clic).
 *
 * References reconnues :
 *   `username`, `password`          champs de l'entree ;
 *   `history:<i>`                    i-eme ancien mot de passe (ordre de `readPasswordHistory`) ;
 *   `typed:<groupe>:<cle>`          champ type (carte, identite, Wi-Fi, compte bancaire, portefeuille, serveur…) ;
 *   `custom:<i>`                     i-eme champ personnalise (ordre de `readCustomFields`) ;
 *   `uri:<i>`                        i-eme adresse de l'entree (`secrets.uris`) ;
 *   `version:<i>:<reference>`        une des references ci-dessus, lue dans la i-eme version precedente
 *                                    (ordre de `readItemHistory`) -- jamais une version d'une version.
 * Toute autre reference, ou un champ vide, donne `null` : rien n'est copie. Module pur, zero E/S.
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import { readCustomFields } from './custom-fields.js';
import { readItemHistory } from './item-history.js';
import { readPasswordHistory } from './password-history.js';
import { TYPED_FIELD_GROUPS, readTypedFields, type TypedFieldGroup } from './typed-fields.js';

/** Index decimal strict (`01`, `-1`, `1e2`, ` 1` refuses) : une reference forgee ne designe pas un autre champ. */
function readIndex(raw: string): number | null {
  return /^(0|[1-9]\d{0,3})$/u.test(raw) ? Number(raw) : null;
}

/** Valeur a copier pour une reference de champ, lue dans les secrets dechiffres ; `null` si rien a copier. */
export function entryFieldValue(secrets: EntrySecrets, ref: string): string | null {
  const nonEmpty = (value: unknown): string | null => (typeof value === 'string' && value.length > 0 ? value : null);
  if (ref === 'username' || ref === 'password') return nonEmpty(secrets[ref]);
  const [kind, first, second, ...rest] = ref.split(':');
  if (kind === 'version' && first !== undefined && second !== undefined) {
    const index = readIndex(first);
    const inner = ref.slice(`version:${first}:`.length);
    // `readItemHistory` retire historiques et versions de chaque version : une reference imbriquee n'y trouve rien.
    const version = index === null ? undefined : readItemHistory(secrets)[index];
    return version === undefined ? null : entryFieldValue(version.secrets, inner);
  }
  if (kind === 'history' && first !== undefined && second === undefined) {
    const index = readIndex(first);
    return index === null ? null : nonEmpty(readPasswordHistory(secrets)[index]?.password);
  }
  if (kind === 'uri' && first !== undefined && second === undefined) {
    const index = readIndex(first);
    const uris = Array.isArray(secrets.uris) ? secrets.uris : [];
    return index === null ? null : nonEmpty(uris[index]);
  }
  if (kind === 'custom' && first !== undefined && second === undefined) {
    const index = readIndex(first);
    return index === null ? null : nonEmpty(readCustomFields(secrets)[index]?.value);
  }
  if (kind === 'typed' && first !== undefined && second !== undefined && rest.length === 0) {
    if (!(TYPED_FIELD_GROUPS as readonly string[]).includes(first)) return null;
    const fields = readTypedFields(secrets, first as TypedFieldGroup);
    return Object.hasOwn(fields, second) ? nonEmpty(fields[second]) : null;
  }
  return null;
}
